import React, { useEffect, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Space, Table, Input, Card, Button, Tabs, Tag, Tooltip, Typography } from "antd";
import { httpRequest } from "../../helpers/api";
import { ColumnsType } from "antd/lib/table";
import useFetchList from "../../hooks/useFetchList";
import HeaderSection from "../../components/HeaderSection";
import Filter from "../../components/Filter";
import NotSet from "../../components/NotSet";
import { EmployeeProperties } from "../../types/employee.type";
import useAuth from "../../hooks/useAuth";
import { Link, useHistory, useParams } from "react-router-dom";
import FilterOffice from "../../components/form/FilterOffice";
import CustomPagination from "../../components/CustomPagination";
import { ReactComponent as Transfer } from "../../assets/Icons/Transfer.svg";
import { capitalizeFirstLetter } from "../../helpers/text";
import History from "./History";
import { TransferListingProperties } from "../../types/transferListing.type";
import useMediaQuery from "../../hooks/useMediaQuery";
import { formatYearToTime } from "../../helpers/format";
import useFilterOffice from "../../hooks/useFilterOffice";

interface Params {
	type?: string;
}

const TransferListingAgent = () => {
	const isBelowLg = useMediaQuery("(max-width: 991px)");
	const filterOfficeId = useFilterOffice();
	const [tab, setTab] = useState("agent");
	const { type } = useParams<Params>();
	const history = useHistory();
	const { isFOPlus } = useAuth();
	const { isLoading, query, data, search, pagination, changePage, setSearch, setQuery } = useFetchList<
		EmployeeProperties,
		{ filterOfficeId?: string }
	>({
		apiRequest: httpRequest,
		endpoint: "employees/custom/agent-inactive",
		initialQuery: {
			filterOfficeId,
		},
	});

	const transferHistory = useFetchList<
		TransferListingProperties,
		{
			filterOriginListingOfficeId?: string;
			includeData?: string;
		}
	>({
		endpoint: "transfer-listings",
		initialQuery: {
			includeData: ["listingTransferHistoryCount", "employee", "user", "office"].join(","),
		},
	});

	useEffect(() => {
		if (type) {
			setTab(type);
		}
	}, [type]);

	const COLUMNS: ColumnsType<EmployeeProperties> = [
		{
			title: "AGENT ID",
			dataIndex: "employeeCode",
			key: "employeeCode",
			width: "10%",
			render: (employeeCode: string, record: EmployeeProperties) => {
				return <Link to={`/agent/${record.userId}`}>{employeeCode?.toUpperCase()}</Link>;
			},
		},
		{
			title: "NAME",
			dataIndex: ["user", "nickName"],
			key: "nickName",
			width: "20%",
			render: (nickname: string) => <>{nickname ? nickname.toUpperCase() : <NotSet />}</>,
		},
		isFOPlus
			? {
					title: "OFFICE",
					dataIndex: ["office", "officeName"],
					key: "officeName",
					width: "20%",
					render: (officeName: string) => <>{officeName ? officeName.toUpperCase() : <NotSet />}</>,
			  }
			: {},
		{
			title: "TOTAL LISTING",
			dataIndex: "totalListingCount",
			key: "totalListingCount",
			width: "15%",
		},
		{
			title: "STATUS",
			dataIndex: ["employeeRole", 0, "status"],
			key: "status",
			width: "10%",
			render(value) {
				return (
					<Tag
						color="error"
						style={{ padding: "4px 12px" }}
					>
						{capitalizeFirstLetter(value)}
					</Tag>
				);
			},
		},
		{
			title: "INACTIVE AT",
			dataIndex: "inactiveAt",
			key: "inactiveAt",
			width: "15%",
			render(_, record: EmployeeProperties) {
				let inactiveAt = undefined;
				if (
					Array.isArray(record.agentHistories) &&
					Array.isArray(record.agentHistories[0]?.newData) &&
					record.agentHistories[0]?.newData[0]?.createdAt
				) {
					inactiveAt = record.agentHistories[0]?.newData[0]?.createdAt;
				}
				return inactiveAt ? formatYearToTime(inactiveAt) : <NotSet />;
			},
		},
		{
			title: "",
			dataIndex: "employeeId",
			key: "action",
			width: "10%",
			render: (_: string, record: EmployeeProperties) => {
				return (
					<Tooltip title={record?.totalListingCount === "0" ? "Can't transfer listing, listing empty" : ""}>
						<Button
							disabled={record?.totalListingCount === "0"}
							type="primary"
							onClick={() => {
								history.push(`/transfer-listing/create/${record.userId}`);
							}}
						>
							<Space>
								<Transfer />
								<span>Transfer Listing</span>
							</Space>
						</Button>
					</Tooltip>
				);
			},
		},
	];

	const _handleChangeOffice = (officeId: string) => {
		if (tab === "agent") {
			setQuery({
				...query,
				filterOfficeId: officeId,
			});
		}
		if (tab === "history") {
			transferHistory.setQuery({
				...query,
				filterOriginListingOfficeId: officeId,
			});
		}
	};

	return (
		<React.Fragment>
			<Space
				direction="vertical"
				size="large"
			>
				<HeaderSection
					hideInfoResponsive
					title="Transfer Listing (For Inactive Agent)"
					tabsComponent={
						<Tabs
							renderTabBar={(props, TabNavList) => (
								<TabNavList
									{...props}
									mobile={true}
								/>
							)}
							activeKey={tab}
							onChange={(newTab) => {
								setTab(newTab);
								history.replace(`/transfer-listing/${newTab}`);
							}}
							onTabClick={(key) => {
								setTab(key);
								history.replace(`/transfer-listing/${key}`);
							}}
							items={[
								{
									key: "agent",
									label: <span>Agent Inactive</span>,
									children: (
										<Space direction="vertical">
											{isBelowLg && (
												<Typography.Text style={{ color: "#8C8C8C" }}>
													Note: Best viewed on mobile in landscape mode
												</Typography.Text>
											)}
											<Filter>
												<Filter.Medium>
													<Input
														className="outer-shadow"
														placeholder="Search by Agent ID or Name..."
														suffix={<SearchOutlined />}
														allowClear
														onChange={(e) => setSearch(e.target.value)}
														value={search}
													/>
												</Filter.Medium>
												{isFOPlus && (
													<Filter.Small>
														<FilterOffice
															value={query.filterOfficeId}
															onChange={_handleChangeOffice}
															showOfficeCode={true}
														/>
													</Filter.Small>
												)}
											</Filter>
											<Card
												bordered={false}
												size="small"
											>
												<Table
													loading={isLoading}
													columns={COLUMNS.filter((v) => v.key)}
													dataSource={data}
													pagination={false}
													rowKey="employeeId"
													scroll={{ x: "100%" }}
												/>
												<CustomPagination
													current={pagination.page}
													total={pagination.totalData}
													defaultPageSize={pagination.perPage}
													pageSize={pagination.perPage}
													onChange={changePage}
												/>
											</Card>
										</Space>
									),
								},
								{
									key: "history",
									label: <span>History Transfer Listing</span>,
									children: (
										<Space direction="vertical">
											{isBelowLg && (
												<Typography.Text style={{ color: "#8C8C8C" }}>
													Note: Best viewed on mobile in landscape mode
												</Typography.Text>
											)}
											<Filter>
												<Filter.Medium>
													<Input
														className="outer-shadow"
														placeholder="Search by Agent ID or Name..."
														suffix={<SearchOutlined />}
														allowClear
														onChange={(e) => transferHistory.setSearch(e.target.value)}
														value={transferHistory.search}
													/>
												</Filter.Medium>
												{isFOPlus && (
													<Filter.Small>
														<FilterOffice
															value={transferHistory.query.filterOriginListingOfficeId}
															onChange={_handleChangeOffice}
															showOfficeCode={true}
														/>
													</Filter.Small>
												)}
											</Filter>

											<History {...transferHistory} />
										</Space>
									),
								},
							]}
						/>
					}
				></HeaderSection>
			</Space>
		</React.Fragment>
	);
};

export default TransferListingAgent;
